// Sağdan açılan form paneli (değişken genişlik, overlay yok)
'use client';

import { useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

type SheetSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const sizeClasses: Record<SheetSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-none',
};

interface SlideOverSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  size?: SheetSize;
  className?: string;
  titleAdornment?: React.ReactNode;
  children: React.ReactNode;
}

export function SlideOverSheet({ open, onClose, title, description, size = 'md', className, titleAdornment, children }: SlideOverSheetProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      contentRef.current?.scrollTo(0, 0);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleKeyDown]);

  return (
    <>
      {/* Invisible backdrop to catch outside clicks */}
      {open && (
        <div
          className="fixed inset-0 top-16 z-30"
          onClick={onClose}
        />
      )}
      <div
        className={cn(
          'fixed right-0 top-16 bottom-0 z-40 w-full bg-background border-l border-border shadow-2xl',
          'flex flex-col transition-transform duration-300 ease-out',
          sizeClasses[size],
          open ? 'translate-x-0' : 'translate-x-full',
          className
        )}
      >
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{title}</h2>
            {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
          </div>
          {titleAdornment}
        </div>
        <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div ref={contentRef} className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
    </div>
    </>
  );
}
